import React, { useEffect, useState } from 'react';
import ChatBot, { BotOptionsContext, getDefaultBotOptions, MessagesContext } from 'react-chatbotify';
import { makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { isJsonParsable } from 'helpers';
import '../../assets/css/chatbot.css';
import chatIcon2 from "../../assets/images/chatLogo2.png";
import { ChatResponse } from 'components/chatbot/ChatResponse';
import { ChatProcessing } from 'components/chatbot/ChatProcessing';
import { t } from 'i18next';
import { getStreamChatService } from 'reduxLib/services/chatBotService';
import { lisaMockResponse } from 'components/dummydata/lisaMockData';
import { chatAuth } from './LisaAuthCallback';
import Snack from './Helpers/Snack';
import { useDispatch, useSelector } from 'react-redux';
import { lisaConstants } from 'reduxLib/constants';
import { toPositiveNum } from 'helpers/lisaHelpers';
import { ChatHeaderMenu } from 'components/chatbot/ChatHeaderMenu';

const useStyles = makeStyles((theme) => ({
    root: {
        '& .rcb-close-chat-icon': {
            display: ['development', 'local'].includes(process.env.REACT_APP_ENV) ? 'none' : ''
        }
    },
    chatBotTitle: {
        height: '100%',
        alignContent: 'center',
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between'
    },
    btn: {
        backgroundColor: 'transparent',
        color: theme.palette.black,
        border: 'none',
        padding: theme.spacing(1),
        // borderRadius: theme.shape.borderRadius,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        flexDirection: 'column',
    },
    msgTextLink: {
        // color: theme.palette.common.white,
        textDecoration: 'underline',
        fontWeight: 'bold'
    },

}));

export const Title = ({ closeChat }) => {
    const classes = useStyles();
    return (
        <div className={classes.chatBotTitle} data-testid="chatTitle">
            {/* Latam Insights and Shipments Assistant */}
            <h2>{t('preview_to_lisa')}</h2>
            {['development', 'local'].includes(process.env.REACT_APP_ENV) ?
                <ChatHeaderMenu closeChat={closeChat} /> : ''}
        </div>
    );
};

const chatLanguages = {
    de: 'de-DE',
    en: 'en-US',
    es: 'es-ES',
    fr: 'fr-FR',
    pt: 'pt-PT',
}

const defaultBotOptions = getDefaultBotOptions();

const config = {
    ...defaultBotOptions,
    // chatButton: { icon: chatIcon2 },
    chatButtonStyle: { ...defaultBotOptions.chatButtonStyle, backgroundImage: `url(${chatIcon2})`, right: '3px', backgroundColor: 'transparent' },
    header: { ...defaultBotOptions.header, avatar: chatIcon2 },
    // tooltip: { mode: 'NEVER' },
    tooltip: { ...defaultBotOptions.tooltip, mode: 'START' },
    // chatHistory: { disabled: true },
    notification: { ...defaultBotOptions.notification, disabled: true },
    fileAttachment: { ...defaultBotOptions.fileAttachment, disabled: true },
    emoji: { ...defaultBotOptions.emoji, disabled: true },
    chatWindow: {
        ...defaultBotOptions.chatWindow,
        autoJumpToBottom: true,
        showScrollbar: true
    },
    chatInput: {
        ...defaultBotOptions.chatInput,
        characterLimit: 1000,
        showCharacterCount: true,
    },
    theme: {
        ...defaultBotOptions.theme,
        // primaryColor: '',
        // secondaryColor: '',
        fontFamily: 'sans-serif'
    },
    voice: {
        ...defaultBotOptions.voice,
        // disabled: !(["local", 'development'].includes(process.env.REACT_APP_ENV)),
        // defaultToggledOn: false,
        // language: "en-US",
        // timeoutPeriod: 10000,
        autoSendDisabled: false,
        autoSendPeriod: 5000,
        // icon: voiceIcon,
    },
    advance: {
        ...defaultBotOptions.advance,
        useCustomMessages: true,
        useCustomBotOptions: true,
    },
};



export const ChatBotifyWithStream = ({ region, user }) => {
    const { t } = useTranslation()
    const language = localStorage.getItem('i18nextLng');
    const dispatch = useDispatch();
    const classes = useStyles();
    const { lisaLoginError } = useSelector(({ lisa }) => lisa);
    const [botOptions, setBotOptions] = useState({ ...config })
    const [messages, setMessages] = useState([]);


    const userManualLink = process.env.REACT_APP_LISA_USER_MANUAL;
    const LinkElement = () => {
        return <div className='rcb-bot-message-entry rcb-bot-message'>
            <div>
                <p>{t('chat_message_intro')}</p>
                <br />
                <p><a href={userManualLink} target='_blank' className={classes.msgTextLink} >{t('click_here')}</a> {t('chat_message_user_manual')}</p>
            </div>
        </div>
    }

    const closeChat = () => {
        setBotOptions({ ...config, isOpen: false });
    }

    useEffect(() => {
        if (messages.length <= 1) {
            setMessages([
                {
                    content: <LinkElement />,
                    sender: 'bot',
                }
            ]);
            config.voice.language = chatLanguages[language]
        }
        setBotOptions({
            ...config,
            isOpen: botOptions.isOpen,
            header: { ...config.header, title: <Title closeChat={closeChat} /> },
            chatInput: { ...config.chatInput, enabledPlaceholderText: t('type_your_message') },
            tooltip: { ...config.tooltip, text: t('chat_btn_text') },
            footer: { ...config.footer, text: t('ai_accuracy_disclaimer') },
        })
    }, [language, botOptions.isOpen]);

    const handleResponse = async (result, params) => {
        const responseData = result?.data;
        const metadata = responseData?.metadata;
        const tableData = responseData?.results;
        const finalAnswer = responseData?.answer;

        const chartRes = metadata?.chart_recommendation;
        let chartData = {};
        if (chartRes?.charts && chartRes.charts?.length) {
            chartRes.charts.map((chart, index) => {
                if (chartData?.[chart?.chart_type] && chart?.recommendation_rank != 'recommended') {
                    return
                }
                const total = chart?.y_axis?.major?.[0]?.data?.reduce((total, val) => total + toPositiveNum(val), 0);
                const data = chart?.x_axis?.data?.map((name, i) => {
                    const trendLineKey = chart?.chart_type == 'line' ? 'major' : 'minor';
                    const itemValue = toPositiveNum(chart?.y_axis?.major?.[0]?.data?.[i]);
                    const percentageValue = (itemValue / total * 100).toFixed(2); // for pie chart
                    return {
                        // axisLabel: chart?.chart_type == 'pie' ? `${percentageValue}% (${name})` : name ?? "",
                        axisLabel: name ?? "",
                        itemValue: itemValue,
                        value: percentageValue, // for pie chart
                        trendlineValue: toPositiveNum(chart?.y_axis?.[trendLineKey]?.[0]?.data?.[i])
                    };
                })
                chartData[chart?.chart_type] = {
                    data,
                    chartTitle: chart?.title,
                    xTitle: chart?.x_axis?.axis_label,
                    yMajorTitle: chart?.y_axis?.major?.[0]?.axis_label,
                    yMinorTitle: chart?.y_axis?.minor?.[0]?.axis_label
                }
            });
        }
        let chartProps = {
            keys: ['itemValue'],
            xKey: 'axisLabel',
            yKey: 'trendlineValue',
            trendLineKey: 'trendlineValue',
            is_chart_applicable: chartRes?.is_chart_applicable,
            chartTypes: Object.keys(chartData)?.map(v => v?.toLowerCase()),
            chartData
        };
        await params.streamMessage(<ChatResponse
            params={params}
            message={finalAnswer}
            tableData={tableData}
            chartProps={chartProps}
            user={user}
            metadata={metadata}
            region={region}
            duration={result?.duration}
        />);

        const chatData = {
            message: finalAnswer,
            tableData: tableData,
            metadata: metadata
        }
        dispatch({ type: lisaConstants.UPDATE_CHAT_DATA, payload: chatData })
        localStorage.setItem('chatSessionId', metadata?.thread_id);
    }

    const getStreamMessage = async (params) => {
        // handleResponse(lisaMockResponse.data, params); return;
        // let chatToken = localStorage.getItem("okta-token-lisa")
        // chatToken = isJsonParsable(chatToken)

        // if (!chatToken?.idToken) {
        //     chatAuth.token.getWithRedirect();
        //     return;
        // }

        const controller = new AbortController();
        const signal = controller.signal;
        await params.injectMessage(<ChatProcessing chatProcess={t('processing')} abortCtlr={controller} />, 'spinner');
        const response = await getStreamChatService({ params, user, region, signal })
        if (response && response?.data) {
            handleResponse(response, params)
        } else {
            localStorage.setItem('chatData', '');
            const errorMsg = response === 'cancelled' ? t('ai_request_cancelled') : t('chatErrorMessage');
            await params.streamMessage(<ChatResponse params={params} message={{ final_answer: errorMsg }} />);
        }
        document.querySelectorAll('.spinnerContainer').forEach(el => el.remove());
    }

    const [flow, setFlow] = useState({
        start: {
            message: '',
            path: 'loop'
        },
        loop: {
            message: (params) => { getStreamMessage(params); },
            path: 'loop'
        }
    });
    const removeLoginError = () => {
        dispatch({ type: lisaConstants.REMOVE_LOGIN_ERROR })
    }

    return (
        <div data-testid="chatbot-container" className={classes.root}>
            <Snack
                open={lisaLoginError}
                severity={'error'}
                message={t(lisaLoginError)}
                handleClose={removeLoginError}
            />
            <BotOptionsContext.Provider
                value={{ botOptions: botOptions, setBotOptions: setBotOptions }}>
                <MessagesContext.Provider value={{ messages: messages, setMessages: setMessages }}>
                    <ChatBot flow={flow} options={botOptions} dataTestId="chatbotWithStream" />
                </MessagesContext.Provider>
            </BotOptionsContext.Provider>
        </div>
    );
};